import { Skeleton, Switch } from "@heroui/react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes"; // Import useTheme from next-themes
import { useEffect, useState } from "react";

interface ThemeSwitchProps {
  isIconOnly?: boolean;
}

/**
 * Theme switcher component that toggles between light and dark modes.
 * Supports both icon-only and labeled variants.
 *
 * @component
 * @param {ThemeSwitchProps} props - Component properties
 * @param {boolean} [props.isIconOnly=false] - When true, hides the "Theme" label
 *
 * @accessibility
 * - Provides aria-label for the switch
 * - Shows loading skeleton during SSR/hydration
 *
 * @returns {React.JSX.Element} Theme switch component with optional label
 */
export function ThemeSwitch({ isIconOnly = false }: ThemeSwitchProps): React.JSX.Element {
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme, resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);

    // Check for cached theme in local storage and apply it
    const cachedTheme = localStorage.getItem("theme");

    if (cachedTheme && cachedTheme !== theme) {
      setTheme(cachedTheme);
    }
  }, [setTheme, theme]);

  if (!mounted)
    return (
      <div className="flex items-center justify-between gap-4 w-full max-w-full" aria-hidden="true">
        {!isIconOnly && <h3 className="pt-0.5 text-wfp-text">Theme</h3>}
        <Skeleton aria-hidden="true" className="rounded-full w-12 h-7" />
      </div>
    );

  return (
    <Switch
      aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      classNames={{
        base: "flex items-center gap-4 w-full max-w-full justify-between",
        wrapper: "order-2 bg-default-200 group-data-[selected=true]:bg-default-300",
        thumb: ["bg-default-400", "group-data-[selected=true]:bg-default-600"],
        label: "order-1 text-tiny ms-0",
      }}
      isSelected={resolvedTheme === "dark"}
      onChange={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setTheme(resolvedTheme === "dark" ? "light" : "dark");
        }
      }}
      thumbIcon={resolvedTheme === "dark" ? <Moon size={14} color="currentColor" aria-hidden="true" /> : <Sun size={14} color="currentColor" aria-hidden="true" />}
    >
      {!isIconOnly && <h3 className="text-wfp-text font-semibold">Theme</h3>}
    </Switch>
  );
}